import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  type FilterTemplate,
  GetTasksSimpleMapOptionsDocument,
  type GetTasksSimpleMapOptionsQuery,
  type GetTasksSimpleMapOptionsQueryVariables,
  type OutgoingInvoice,
  type RequestForInvoicing,
  SqlOperator,
  TimeAndMaterialsFilterOptionsDocument,
  type TimeAndMaterialsFilterOptionsQuery,
  type TimeAndMaterialsFilterOptionsQueryVariables,
  TimeAndMaterialsInvoiceDataDocument,
  type TimeAndMaterialsInvoiceDataQuery,
  type TimeAndMaterialsInvoiceDataQueryVariables,
  TimeMaterialsInvoiceDataGroupByEnum,
  TimeTrackingItemWhereColumn,
  type UpdateOutgoingInvoiceInput,
} from "@src/__generated__/graphql";
import type { InvoiceStore } from "@src/components/modules/invoices/Create/store";
import { client } from "@src/services/apollo-client";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import type { FilterModelType } from "@src/stores/FilterTemplatesStore";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { toApiDate } from "@src/utils/dates";
import mapToOptions from "@src/utils/map-to-options";
import { getQueryParamOptional } from "@src/utils/routing";
import type { DateRange } from "@src/utils/types";
import { endOfMonth, startOfMonth } from "date-fns";
import { action, computed, makeObservable, observable } from "mobx";
import Router from "next/router";
import { TimeAndMaterialsTableState } from "./table-state";

export type GenerateFromOption = "budget-item" | "grouping" | undefined;

export class TimeAndMaterialsProjectCardStore implements BaseStore {
  appStore: AppStore;

  @observable fetchingData = false;
  @observable workTableState: TimeAndMaterialsTableState;
  @observable expenseTableState: TimeAndMaterialsTableState;
  @observable fetchingFilters = false;
  @observable savingFiltersTemplate = false;
  @observable projectTitle: string = "";
  @observable projectId: string | undefined = undefined;
  @observable generateFrom: GenerateFromOption = undefined;
  @observable groupBy: TimeMaterialsInvoiceDataGroupByEnum =
    TimeMaterialsInvoiceDataGroupByEnum.Task;
  @observable search = "";
  @observable period: DateRange = {
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date()),
  };

  filtersTemplate: FilterTemplate | undefined;

  where = new Filters<TimeTrackingItemWhereColumn>(
    [
      new Filter({
        column: TimeTrackingItemWhereColumn.UserId,
        title: t`User`,
        options: [],
        value: [],
        operator: SqlOperator.In,
      }),
      new Filter({
        column: TimeTrackingItemWhereColumn.OurWorkBudgetItemId,
        title: t`Our Work Budget Item`,
        options: [],
        value: [],
        operator: SqlOperator.In,
        onChange: (owbiIds) => {
          if (!Array.isArray(owbiIds)) return;
          this.fetchTaskFilterOptions(owbiIds);
        },
      }),
      new Filter({
        column: TimeTrackingItemWhereColumn.TaskId,
        title: t`Task`,
        options: [],
        value: [],
        operator: SqlOperator.In,
        noOptionsMessage: t`Please select budget item first.`,
      }),
      new Filter({
        column: TimeTrackingItemWhereColumn.TimeTrackingWorkTypeId,
        title: t`Position`,
        options: [],
        value: [],
        operator: SqlOperator.In,
      }),
    ],
    {
      onChange: () => {
        this.fetchInvoiceData();
      },
    },
  );

  constructor(appStore: AppStore, invoiceStore: InvoiceStore) {
    makeObservable(this);
    this.appStore = appStore;
    this.workTableState = new TimeAndMaterialsTableState(
      "time-and-materials-work",
      this,
      invoiceStore,
    );
    this.expenseTableState = new TimeAndMaterialsTableState(
      "time-and-materials-expense",
      this,
      invoiceStore,
    );
  }

  @computed
  get timeMaterialsInvoiceData(): UpdateOutgoingInvoiceInput["timeMaterialsInvoiceData"] {
    if (!this.projectId) return undefined;

    return {
      from: toApiDate(this.period.start),
      to: toApiDate(this.period.end),
      groupBy: this.groupBy,
      project_id: this.projectId,
      ids: this.workTableState.selectedIds.concat(
        this.workTableState.selectedSubrowIds,
      ),
      expense_ids: this.expenseTableState.selectedIds.concat(
        this.expenseTableState.selectedSubrowIds,
      ),
      commission_data: this.expenseTableState.commissionData,
    };
  }

  @computed get groupByLabel(): string {
    return (
      Object.entries(TimeMaterialsInvoiceDataGroupByEnum).find(
        (values) => values[1] === this.groupBy,
      )?.[0] ?? t`None`
    );
  }

  @computed get owbiFilter() {
    return this.where.filtersByColumn.get(
      TimeTrackingItemWhereColumn.OurWorkBudgetItemId,
    );
  }

  @computed get selectedGroupByKey() {
    const match = Object.entries(TimeMaterialsInvoiceDataGroupByEnum).find(
      ([_, value]) => value === this.groupBy,
    )?.[0];
    if (!match) return "";
    return match === "Date" ? t`Days` : `${match}s`;
  }

  @computed get isGenerateFromGroupingDisabled(): boolean {
    return (
      this.workTableState.isGenerateFromGroupingDisabled ||
      this.expenseTableState.isGenerateFromGroupingDisabled
    );
  }

  @computed get totalToInvoice() {
    return (
      this.workTableState.totalToInvoice + this.expenseTableState.totalToInvoice
    );
  }

  @action setPeriod(newPeriod: DateRange) {
    this.period = newPeriod;
  }

  @action setGroupBy(newGroupBy: TimeMaterialsInvoiceDataGroupByEnum) {
    this.groupBy = newGroupBy;
  }

  @action setGenerateFrom(option: GenerateFromOption) {
    this.generateFrom = option;
  }

  @action.bound handleSearch(newSearch: string) {
    this.search = newSearch;
    this.fetchInvoiceData();
  }

  @action.bound async fetchInvoiceData(projectId?: string) {
    if (!projectId && !this.projectId) {
      this.fetchingData = false;
      this.appStore.UIStore.toast({
        title: t`Missing project ID.`,
        status: "warning",
      });
      return;
    }

    try {
      this.fetchingData = true;

      const { data, error } = await client
        .query<
          TimeAndMaterialsInvoiceDataQuery,
          TimeAndMaterialsInvoiceDataQueryVariables
        >({
          query: TimeAndMaterialsInvoiceDataDocument,
          variables: {
            groupBy: this.groupBy,
            from: toApiDate(this.period.start),
            to: toApiDate(this.period.end),
            project_id: projectId ?? this.projectId ?? "",
            outgoing_invoice_id: getQueryParamOptional("id", Router.query),
            filters: {
              where: this.where.asWhereParam,
              search: this.search,
            },
          },
        })
        .then((result) => {
          this.workTableState.setSelectedIds([]);
          this.expenseTableState.setSelectedIds([]);
          this.fetchingData = false;
          return result;
        });

      if (error || !data?.timeMaterialsInvoiceData) {
        this.projectTitle = "";
        this.workTableState.setTableData([]);
        this.expenseTableState.setTableData([]);
        this.fetchingData = false;
        return;
      }

      this.projectId = data.timeMaterialsInvoiceData.project.id;
      this.projectTitle = data.timeMaterialsInvoiceData.project.title;
      this.workTableState.setTableData(
        data.timeMaterialsInvoiceData.ourWorkBudgetItems,
      );
      this.expenseTableState.setTableData(
        data.timeMaterialsInvoiceData.expenseBudgetItems,
      );
    } catch (_) {}
  }

  @action.bound async fetchFilterOptions() {
    this.fetchingFilters = true;

    try {
      const { data, error } = await client.query<
        TimeAndMaterialsFilterOptionsQuery,
        TimeAndMaterialsFilterOptionsQueryVariables
      >({
        query: TimeAndMaterialsFilterOptionsDocument,
        variables: {
          projectId: this.projectId,
        },
      });

      this.fetchingFilters = false;

      if (!data || error) {
        this.appStore.UIStore.toast({
          title: t`Fetching filters failed, please reload page.`,
          status: "error",
        });
        return;
      }

      this.where.filtersByColumn
        .get(TimeTrackingItemWhereColumn.UserId)
        ?.setOptions(mapToOptions.users(data.userSimpleMap));

      this.where.filtersByColumn
        .get(TimeTrackingItemWhereColumn.TimeTrackingWorkTypeId)
        ?.setOptions(mapToOptions.workTypes(data.timeTrackingWorkTypes));

      this.where.filtersByColumn
        .get(TimeTrackingItemWhereColumn.OurWorkBudgetItemId)
        ?.setOptions(
          mapToOptions.budgetItems(data.ourWorkBudgetItemsSimpleMap),
        );
    } catch (e) {
      console.error(e);
      this.fetchingFilters = false;
    }
  }

  @action async fetchTaskFilterOptions(owbiIds: string[]) {
    this.where.filtersByColumn
      .get(TimeTrackingItemWhereColumn.TaskId)
      ?.setOptions([]);

    try {
      const { data, error } = await client.query<
        GetTasksSimpleMapOptionsQuery,
        GetTasksSimpleMapOptionsQueryVariables
      >({
        query: GetTasksSimpleMapOptionsDocument,
        variables: {
          our_work_budget_item_id: owbiIds,
        },
      });

      if (!data.tasksSimpleMap || error) {
        this.appStore.UIStore.toast({
          title: t`Fetching task filters failed, please reload page.`,
          status: "error",
        });
        return;
      }

      this.where.filtersByColumn
        .get(TimeTrackingItemWhereColumn.TaskId)
        ?.setOptions(
          data.tasksSimpleMap.map((task) => ({
            value: task.id,
            label: task.name,
          })),
        );
    } catch (_) {}
  }

  @action setInvoiceData(
    invoiceData:
      | OutgoingInvoice["timeMaterialsInvoiceData"]
      | RequestForInvoicing["timeMaterialsInvoiceData"],
  ) {
    if (!invoiceData) return;
    this.setGroupBy(invoiceData.groupBy);
    this.setPeriod({
      start: new Date(invoiceData.from),
      end: new Date(invoiceData.to),
    });
  }

  @action setFilterTemplate(template: FilterTemplate) {
    this.filtersTemplate = template;
  }

  @action.bound async setFiltersFromTemplateParams(
    template: FilterTemplate,
    onAfterChange: () => void,
  ) {
    this.setFilterTemplate(template);
    if (!template?.params) return;

    try {
      const params = JSON.parse(template.params);
      if ("where" in params) {
        this.where.fromURLSearchParam(params, { skipOnChange: true });
        await this.fetchInvoiceData();
        onAfterChange();
      }
    } catch (e) {
      console.error(e);
      captureEvent({
        message: `FE: Something went wrong while resolving filters.`,
      });
      this.appStore.UIStore.toast({
        status: "error",
        title: t`Something went wrong, please reload and try again.`,
      });
    }
  }

  @action async saveFiltersAsTemplate(
    modelId: string,
    modelType: FilterModelType,
    templateId?: string,
  ) {
    try {
      const params = JSON.stringify({
        where: this.where.asURLSearchParam.where,
      });

      this.savingFiltersTemplate = true;

      await this.appStore.filterTemplatesStore.saveTemplate(
        t`Time & Materials filters template`,
        modelId,
        modelType,
        params,
        templateId,
      );
      this.savingFiltersTemplate = false;
    } catch (_) {
      this.savingFiltersTemplate = false;
    }
  }
}
